import React, { useCallback, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import PhotoImage from "./PhotoImage";

/**
 * Visor a pantalla completa para una galería del portafolio.
 *
 * Teclado: flechas para avanzar o retroceder, Escape para cerrar. En el
 * celular, deslizar a los lados cambia de foto. Al abrir, el foco va al botón
 * de cerrar y al cerrar vuelve a la foto desde la que se abrió, para que la
 * navegación con teclado no pierda el lugar.
 *
 * Usa el srcSet de la foto con `sizes="100vw"`: el navegador elige el WebP de
 * 1600 en pantallas grandes y no descarga de más en el teléfono.
 */
const FotoLightbox = ({ fotos, indice, onCambiar, onCerrar }) => {
  const cerrarRef = useRef(null);
  const toqueX = useRef(null);
  const abierto = indice !== null && indice >= 0 && indice < fotos.length;

  const ir = useCallback(
    (paso) => onCambiar((indice + paso + fotos.length) % fotos.length),
    [indice, fotos.length, onCambiar]
  );

  useEffect(() => {
    if (!abierto) return undefined;
    const previo = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    cerrarRef.current?.focus();

    const onKey = (event) => {
      if (event.key === "Escape") onCerrar();
      else if (event.key === "ArrowRight") ir(1);
      else if (event.key === "ArrowLeft") ir(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
      if (previo && typeof previo.focus === "function") previo.focus();
    };
  }, [abierto, ir, onCerrar]);

  if (!abierto) return null;
  const foto = fotos[indice];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Foto ${indice + 1} de ${fotos.length}`}
      className="fixed inset-0 z-[60] flex items-center justify-center bg-navy-950/95 backdrop-blur-sm"
      onClick={onCerrar}
      onTouchStart={(event) => {
        toqueX.current = event.touches[0].clientX;
      }}
      onTouchEnd={(event) => {
        if (toqueX.current === null) return;
        const delta = event.changedTouches[0].clientX - toqueX.current;
        toqueX.current = null;
        if (Math.abs(delta) > 50) ir(delta < 0 ? 1 : -1);
      }}
    >
      <figure className="flex max-h-full max-w-full flex-col items-center px-4 py-16" onClick={(e) => e.stopPropagation()}>
        <PhotoImage
          key={foto.id}
          foto={foto}
          sizes="100vw"
          priority
          className="max-h-[82vh] w-auto max-w-full object-contain"
        />
        <figcaption className="mt-4 max-w-2xl text-center text-sm text-navy-200">
          <span className="font-mono tabular-nums text-sand-300">
            {indice + 1} / {fotos.length}
          </span>
          {foto.alt ? <span className="ml-3">{foto.alt}</span> : null}
        </figcaption>
      </figure>

      <button
        ref={cerrarRef}
        type="button"
        onClick={onCerrar}
        aria-label="Cerrar"
        className="absolute right-4 top-4 rounded-full p-3 text-white transition hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sand-300"
      >
        <X className="h-6 w-6" aria-hidden="true" />
      </button>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          ir(-1);
        }}
        aria-label="Foto anterior"
        className="absolute left-2 top-1/2 hidden -translate-y-1/2 rounded-full p-3 text-white transition hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sand-300 sm:block"
      >
        <ChevronLeft className="h-8 w-8" aria-hidden="true" />
      </button>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          ir(1);
        }}
        aria-label="Foto siguiente"
        className="absolute right-2 top-1/2 hidden -translate-y-1/2 rounded-full p-3 text-white transition hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sand-300 sm:block"
      >
        <ChevronRight className="h-8 w-8" aria-hidden="true" />
      </button>
    </div>
  );
};

export default FotoLightbox;
